<template>
  <div class="relative w-full h-full flex items-center justify-center">
    <div class="h-full flex-1 flex items-center justify-center relative px-6">
      <div
        class="h-4/5 w-full max-w-[1224px] relative -translate-y-[10%] shadow-1 bg-white rounded-xl overflow-hidden"
        v-if="fileModel"
      >
        <div class="w-full h-full overflow-hidden">
          <div id="imageView" class="relative w-full h-full">
            <canvas ref="canvas" class="absolute inset-0"></canvas>
          </div>
        </div>
      </div>
      <div v-else class="flex items-center justify-center">
        <FuncTypeUpload
          class="h-[240px] max-w-[90%] mx-auto z-[2] hidden sm:block lg:max-w-[1080px] lg:h-[280px]"
          :funcType="route.params.funcType"
        ></FuncTypeUpload>
      </div>
      <div
        v-if="fileModel"
        v-show="!(fileModel.status >= uploadStatus.lodingImg)"
        class="absolute left-1/2 bottom-10 -translate-x-1/2 bg-white shadow-6 h-[88px] rounded-[20px] px-4 w-max"
      >
        <div class="relative h-full flex items-center justify-between gap-x-4">
          <EnhanceBaseControls
            class="flex-1 flex justify-center"
            ref="baseControlsRef"
            :minScale="minScale"
            :maxScale="maxScale"
            @change="baseCtlScaleChange"
          ></EnhanceBaseControls>
          <div class="h-[33px] bg-gray-line w-px"></div>
          <div class="h-14 flex items-center rounded-xl px-4 gap-[10px]">
            <img
              src="/public/assets/images/enhance/Strength.svg"
              alt="24"
              height="24"
              width="24"
            />
            <span>{{ $t("enhance-strength") }}</span>
            <input
              :class="applyEnabled ? '' : 'cursor-not-allowed'"
              type="range"
              v-model="strength"
              style="width: 100px"
              :min="0"
              :max="5"
              @change="strengthChange"
              :step="0.1"
              :disabled="!applyEnabled"
            />
            <span class="w-5 inline-block">{{ strength }}</span>
          </div>
          <!-- <div class="h-[33px] bg-gray-line w-px"></div> -->
          <!-- <button
            class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:bg-gray-300"
            @click="exportImage"
            :disabled="!processedImage"
          >
            导出
          </button> -->
          <EnhanceApply @click="onApply" :enable="applyEnabled&&processedImage" :text="$t('enhance-base-apply')"></EnhanceApply>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import {
  minScale,
  maxScale,
  imageAccept,
  maxUploadCount,
} from "@/utils/config";
import { uploadStatus } from "@/utils/type";
import { base64ToBlob } from "@/utils/index";
import { useMainStore } from "@/store/index";
import { useUploadStore } from "@/store/upload";
const { t } = useI18n();
const uploadStore = useUploadStore();
const route = useRoute();
const mainStore = useMainStore();
const { funcTypeMap } = useFuncType();
const strength = ref(1);
const fileModel = ref(null);
const { isLogin, isVip, isNormalVip, userInfo } = useUserInfo();

// 添加 canvas 相关的响应式变量
const canvas = ref(null);
const ctx = ref(null);
const isDragging = ref(false);
const lastX = ref(0);
const lastY = ref(0);
const scale = ref(1);
const offsetX = ref(0);
const offsetY = ref(0);
const originOffsetX = ref(0);
const originOffsetY = ref(0);
const sourceImageData = ref(null);
const applyEnabled = ref(true);
// 添加图像引用
const currentImage = ref(null);
// 添加一个引用来存储锐化后的图像
const processedImage = ref(null);
const baseControlsRef = ref(null);
const blobData = ref()
const debounce = useDebounceFn(resize, 300);

// 初始化 canvas
onMounted(() => {
  const images = [...uploadStore.images];
  // console.log(images);

  if (images.length) {
    fileModel.value = images[0];
    setTimeout(() => {
      initCanvas(images[0]);
    }, 10);
  }
  window.addEventListener("uploadLocalFilesSuccess", uploadLocalFilesSuccess);
  window.addEventListener("resize", debounce);
  window.addEventListener("updateLinkEvent", updateLinkEvent);
});
onUnmounted(() => {
  window.removeEventListener(
    "uploadLocalFilesSuccess",
    uploadLocalFilesSuccess
  );
  window.removeEventListener("resize", debounce);
  window.removeEventListener("updateLinkEvent", updateLinkEvent);
});
function resize() {
  resizeCanvas();
}
function resizeCanvas() {
  if (!currentImage.value || !canvas.value) return;

  const container = document.getElementById("imageView");
  if (!container) return;
  const canvasEl = canvas.value;
  canvasEl.width = container.clientWidth;
  canvasEl.height = container.clientHeight;

  const aspectRatio = currentImage.value.height / currentImage.value.width;
  let newWidth = 0;
  let newHeight = 0;

  if (aspectRatio >= 1) {
    newHeight = container.clientHeight;
    newWidth = Math.floor(newHeight / aspectRatio);
  } else {
    newWidth = container.clientWidth;
    newHeight = Math.floor(newWidth * aspectRatio);
  }

  // 计算居中偏移量，考虑缩放比例
  const scaledWidth = newWidth * scale.value;
  const scaledHeight = newHeight * scale.value;
  
  offsetX.value = (canvasEl.width - scaledWidth) / 2;
  offsetY.value = (canvasEl.height - scaledHeight) / 2;

  // 保存原始偏移量
  originOffsetX.value = (canvasEl.width - newWidth) / 2;
  originOffsetY.value = (canvasEl.height - newHeight) / 2;

  // 如果缩放比例为1，重置位置到中心
  if (scale.value === 1) {
    offsetX.value = originOffsetX.value;
    offsetY.value = originOffsetY.value;
  }

  redrawCanvas();
}

// canvas 初始化函数
function initCanvas(file) {
  const canvasEl = canvas.value;
  if (!canvasEl) return;

  ctx.value = canvasEl.getContext("2d");
  const reader = new FileReader();

  reader.onload = function (event) {
    const img = new Image();
    img.onload = function () {
      strength.value = 1;
      currentImage.value = img;
      processedImage.value = null; // 重置处理后的图像

      // 初始化时调用 resize 函数来设置尺寸和位置
      resizeCanvas();
      setStrength(1)
    };
    img.src = event.target.result;
  };
  reader.readAsDataURL(file);

  // 鼠标事件监听
  canvasEl.addEventListener("mousedown", startDragging);
  canvasEl.addEventListener("mousemove", drag);
  canvasEl.addEventListener("mouseup", stopDragging);
  canvasEl.addEventListener("mouseleave", stopDragging);
  canvasEl.addEventListener("wheel", handleZoom);
}

// 拖动相关函数
function startDragging(e) {
  isDragging.value = true;
  lastX.value = e.clientX;
  lastY.value = e.clientY;
}

function drag(e) {
  if (!isDragging.value) return;

  const deltaX = e.clientX - lastX.value;
  const deltaY = e.clientY - lastY.value;

  offsetX.value += deltaX;
  offsetY.value += deltaY;

  lastX.value = e.clientX;
  lastY.value = e.clientY;

  redrawCanvas();
}

function stopDragging() {
  isDragging.value = false;
}

// 缩放处理函数
function handleZoom(e) {
  e.preventDefault();

  const delta = e.deltaY;
  const scaleChange = delta > 0 ? -10 : 10;

  // 计算鼠标位置相对于 canvas 的坐标
  const rect = canvas.value.getBoundingClientRect();
  const mouseX = e.clientX - rect.left;
  const mouseY = e.clientY - rect.top;

  // 更新缩放和偏移，使用整数计算避免浮点误差
  const currentScale = Math.round(scale.value * 100);
  const newScale = currentScale + scaleChange;

  if (newScale >= 50 && newScale <= 250) {
    // 调整偏移以保持鼠标位置不变
    const ratio = newScale / currentScale;
    const dx = (mouseX - offsetX.value) * (ratio - 1);
    const dy = (mouseY - offsetY.value) * (ratio - 1);

    offsetX.value -= dx;
    offsetY.value -= dy;
    scale.value = newScale / 100;
    if (scale.value === 1) {
      offsetX.value = originOffsetX.value;
      offsetY.value = originOffsetY.value;
    }
    baseControlsRef.value.setScale(newScale);
    redrawCanvas();
  }
}

// 重绘 canvas
function redrawCanvas() {
  if (!ctx.value || !currentImage.value) return;

  const canvasEl = canvas.value;
  const img = processedImage.value || currentImage.value;
  const aspectRatio = img.height / img.width;

  let newWidth = 0;
  let newHeight = 0;
  if (aspectRatio >= 1) {
    newHeight = canvasEl.height;
    newWidth = Math.floor(newHeight / aspectRatio);
  } else {
    newWidth = canvasEl.width;
    newHeight = Math.floor(newWidth * aspectRatio);
  }

  // 清除画布
  ctx.value.clearRect(0, 0, canvasEl.width, canvasEl.height);

  // 绘制图像
  ctx.value.drawImage(
    img,
    offsetX.value,
    offsetY.value,
    newWidth * scale.value,
    newHeight * scale.value
  );

  // 只在初始绘制或重新锐化时保存图像数据
  if (!processedImage.value) {
    sourceImageData.value = ctx.value.getImageData(
      0,
      0,
      canvasEl.width,
      canvasEl.height
    );
  }
}

function uploadLocalFilesSuccess(e) {
  const files = e.detail;
  if (files.length) {
    window.dispatchEvent(
      new CustomEvent("downloadUrlEvent", {
        detail: "",
      })
    );
    applyEnabled.value = true;
    fileModel.value = files[0];
    setTimeout(() => {
      initCanvas(files[0]);
    }, 10);
  }
}

function baseCtlScaleChange(scaleVal) {
  // 确保 scaleVal 是整数
  const newScale = Math.round(scaleVal);
  const oldScale = scale.value;
  scale.value = newScale / 100;

  // 计算图片中心点
  const canvasEl = canvas.value;
  const img = processedImage.value || currentImage.value;
  const aspectRatio = img.height / img.width;
  
  let imgWidth = 0;
  let imgHeight = 0;
  if (aspectRatio >= 1) {
    imgHeight = canvasEl.height;
    imgWidth = Math.floor(imgHeight / aspectRatio);
  } else {
    imgWidth = canvasEl.width;
    imgHeight = Math.floor(imgWidth * aspectRatio);
  }

  // 计算图片中心点
  const centerX = offsetX.value + (imgWidth * oldScale) / 2;
  const centerY = offsetY.value + (imgHeight * oldScale) / 2;

  // 计算新的偏移量，使中心点保持不变
  offsetX.value = centerX - (imgWidth * scale.value) / 2;
  offsetY.value = centerY - (imgHeight * scale.value) / 2;

  // 如果缩放比例为1，重置位置到中心
  if (scale.value === 1) {
    offsetX.value = originOffsetX.value;
    offsetY.value = originOffsetY.value;
  }

  redrawCanvas();
}
const updateLinkEvent = () => {
    if (blobData.value) {
        const url = URL.createObjectURL(blobData.value);
        window.dispatchEvent(
          new CustomEvent("downloadUrlEvent", {
            detail: { url, suffix: "png" },
          })
        );
    }
}
const checkWeekCount = () => {
    if (!isVip.value) {
      const num = 10 - (userInfo.value.removalCount ?? 0);
      if (num <= 0) {
          return false;
      }
    } else if (isNormalVip.value) {
        const num = 10 - mainStore.weekCount > 40 ? 0 : 40 - mainStore.weekCount;
        if (num <= 0) {
            return false;
        }
    }
    return true
  }
function onApply() {

  if (!applyEnabled.value) {
    return;
  }
  useLogEvent({ eventName: `apply-${funcTypeMap.ai_sharpen_image}` });

  if (!processedImage.value) return;
  
  applyEnabled.value = false;
  // 创建临时canvas，使用原始图像尺寸
  const tempCanvas = document.createElement("canvas");
  tempCanvas.width = currentImage.value.width;
  tempCanvas.height = currentImage.value.height;
  const tempCtx = tempCanvas.getContext("2d");
  tempCtx.drawImage(
    currentImage.value,
    0,
    0,
    tempCanvas.width,
    tempCanvas.height
  );
  const imageData = tempCtx.getImageData(
    0,
    0,
    tempCanvas.width,
    tempCanvas.height
  );
  const resultData = unsharpMask(imageData, 4, strength.value, 0);

  tempCtx.putImageData(resultData, 0, 0);
  let dataURL = ''
  if (isLogin.value) {
    if (!checkWeekCount()) {
      dataURL = addWaterMark(tempCanvas);
    }else {
      window.dispatchEvent(
      new CustomEvent("updateUseInfoEvent", {
        // 提交相关处理参数，如相关表单
        detail: "",
      })
    );
    dataURL = tempCanvas.toDataURL("image/png");
    }
  }else {
    dataURL = addWaterMark(tempCanvas);
  }
  // dataURL = tempCanvas.toDataURL("image/png");
  blobData.value = base64ToBlob(dataURL);
  let url = URL.createObjectURL(blobData.value);
  window.dispatchEvent(
    new CustomEvent("downloadUrlEvent", {
      detail: { url, suffix: "png" },
    })
  );
  Message.success(t("enhance-task-completed"));
}
function strengthChange(value) {
  setStrength(value.target.valueAsNumber)
}
function setStrength(value) {
  if (!sourceImageData.value) return;
console.log(66666);

  // 创建一个临时canvas来存储锐化后的图像
  const tempCanvas = document.createElement("canvas");
  tempCanvas.width = currentImage.value.width;
  tempCanvas.height = currentImage.value.height;
  const tempCtx = tempCanvas.getContext("2d");
  tempCtx.drawImage(
    currentImage.value,
    0,
    0,
    tempCanvas.width,
    tempCanvas.height
  );
  const imageData = tempCtx.getImageData(
    0,
    0,
    tempCanvas.width,
    tempCanvas.height
  );
  const resultData = unsharpMask(imageData, 4, value, 0);

  // 将锐化后的数据绘制到临时canvas
  tempCtx.putImageData(resultData, 0, 0);

  // 创建新的Image对象存储锐化后的图像
  const processedImg = new Image();
  processedImg.onload = () => {
    processedImage.value = processedImg;
    redrawCanvas(); // 重新绘制使用锐化后的图像
  };
  processedImg.src = tempCanvas.toDataURL();
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function unsharpMask(srcData, radius, amount, threshold) {
  const w = srcData.width,
    h = srcData.height;
  const src = srcData.data;
  const dst = new Uint8ClampedArray(src);
  const blur = new Uint8ClampedArray(src);

  function makeKernel(r) {
    const len = Math.ceil(r) * 2 + 1;
    const sigma = r / 3;
    const kernel = new Float32Array(len);
    const half = (len - 1) / 2;
    let sum = 0;
    for (let i = 0; i < len; i++) {
      const x = i - half;
      const v = Math.exp((-0.5 * (x * x)) / (sigma * sigma));
      kernel[i] = v;
      sum += v;
    }
    for (let i = 0; i < len; i++) kernel[i] /= sum;
    return { kernel, half: Math.floor(half) };
  }

  const { kernel, half } = makeKernel(radius);

  function blurPass(srcArr, tmpArr, horizontal) {
    const step = horizontal ? 1 : w;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        let r = 0,
          g = 0,
          b = 0,
          a = 0;
        for (let k = -half; k <= half; k++) {
          const idx =
            (horizontal ? clamp(x + k, 0, w - 1) : x) +
            (horizontal ? y : clamp(y + k, 0, h - 1)) * w;
          const offs = idx * 4;
          const weight = kernel[k + half];
          r += srcArr[offs] * weight;
          g += srcArr[offs + 1] * weight;
          b += srcArr[offs + 2] * weight;
          a += srcArr[offs + 3] * weight;
        }
        const dstOff = (y * w + x) * 4;
        tmpArr[dstOff] = r;
        tmpArr[dstOff + 1] = g;
        tmpArr[dstOff + 2] = b;
        tmpArr[dstOff + 3] = a;
      }
    }
  }

  blurPass(src, blur, true);
  blurPass(blur, blur, false);

  for (let i = 0; i < src.length; i += 4) {
    for (let c = 0; c < 3; c++) {
      const diff = src[i + c] - blur[i + c];
      const val =
        Math.abs(diff) < threshold ? src[i + c] : src[i + c] + diff * amount;
      dst[i + c] = clamp(val, 0, 255);
    }
    dst[i + 3] = src[i + 3];
  }

  return new ImageData(dst, w, h);
}

// 添加导出功能
function exportImage() {
  if (!processedImage.value || !sourceImageData.value) return;

  // 创建临时canvas，使用原始图像尺寸

  const tempCanvas = document.createElement("canvas");
  tempCanvas.width = currentImage.value.width;
  tempCanvas.height = currentImage.value.height;
  const tempCtx = tempCanvas.getContext("2d");
  tempCtx.drawImage(
    currentImage.value,
    0,
    0,
    tempCanvas.width,
    tempCanvas.height
  );
  const imageData = tempCtx.getImageData(
    0,
    0,
    tempCanvas.width,
    tempCanvas.height
  );
  const resultData = unsharpMask(imageData, 4, strength.value, 0);

  tempCtx.putImageData(resultData, 0, 0);
  let url =addWaterMark(tempCanvas)
  // 创建下载链接
  const link = document.createElement("a");
  link.download = `sharpened_${fileModel.value?.name || "image"}.png`;
  // link.href = tempCanvas.toDataURL("image/png");
  link.href = url

  // 触发下载
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
function addWaterMark(
  canvas,
  str = ["PicMa"],
  density=6,
  fontSize= 12
) {
  const ctx = canvas.getContext("2d");
  let imgWidth = canvas.width;
  let imgHeight = canvas.height;
  if (imgWidth >= 3456) {
    fontSize = 190;
  } else if (imgWidth >= 2700) {
    fontSize = 140;
  } else if (imgWidth >= 2000) {
    //
    fontSize = 110;
  } else if (imgWidth >= 1436) {
    fontSize = 90;
    density = 5;
  } else if (imgWidth >= 1080) {
    //
    fontSize = 50;
  } else if (imgWidth >= 800) {
    fontSize = 40;
  } else if (imgWidth >= 500) {
    //
    fontSize = 34;
  } else {
    fontSize = 14;
  }

  ctx.font = fontSize + "px Roboto";
  var fontcolor = "255, 255, 255, 0.2";

  ctx.fillStyle = "rgba(" + fontcolor + ")";
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";

  //文字坐标

  const maxPx = Math.max(imgWidth, imgHeight);

  const stepPx = Math.floor(maxPx / density);

  let arrayX = [0]; //初始水印位置 canvas坐标 0 0 点
  while (arrayX[arrayX.length - 1] < maxPx / 2) {
    arrayX.push(arrayX[arrayX.length - 1] + stepPx);
  }
  arrayX.push(
    ...arrayX.slice(1, arrayX.length).map((el) => {
      return -el;
    })
  );

  // console.log(arrayX);

  for (let i = 0; i < arrayX.length; i++) {
    for (let j = 0; j < arrayX.length; j++) {
      ctx.save();
      ctx.translate(imgWidth / 2, imgHeight / 2); ///画布旋转原点 移到 图片中心
      ctx.rotate(-Math.PI / 5);
      if (str.length > 3) {
        str = str.slice(0, 3);
      }
      str.forEach((el, index) => {
        let offsetY = fontSize * index + 2;
        ctx.fillText(el, arrayX[i], arrayX[j] + offsetY);
      });
      ctx.restore();
    }
  }

  // 获取图片的base64编码
  return ctx.canvas.toDataURL("image/png");
}
</script>
<style lang="scss" scoped>
input[type="range"] {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 2px;
  border-radius: 5px;
  background-color: $text-deep;
}
input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 12px;
  height: 12px;
  background-color: $text-deep;
  border-radius: 50%;
  cursor: pointer;
}
input[type="range"]::-moz-range-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 12px;
  height: 12px;
  background-color: $text-deep;
  border-radius: 50%;
  cursor: pointer;
  border: none;
}
input[type="range"]::-moz-range-track {
  height: 2px;
  border-radius: 5px;
  background-color: $text-deep;
}
#imageView {
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
}

canvas {
  touch-action: none;
}
</style>
